/**
 * AdminStats Component
 * Dashboard statistics cards showing system metrics
 * Displays: total users, active users, photos, albums, and storage usage
 *
 * @module components/admin/AdminStats
 */

import React, { useEffect } from 'react';
import { AdminStats as AdminStatsType } from '@/types';
import { logger } from '@/utils/logger';

// ============================================================================
// TYPES
// ============================================================================

interface AdminStatsProps {
  stats?: AdminStatsType;
  isLoading?: boolean;
}

// ============================================================================
// STAT CARD COMPONENT
// ============================================================================

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  isLoading?: boolean;
  trend?: {
    value: number;
    direction: 'up' | 'down';
  };
}

/**
 * Individual stat card component
 */
const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  isLoading = false,
  trend,
}) => {
  return (
    <div className="rounded-lg border border-walrus-gray bg-walrus-charcoal p-6 transition-all hover:border-walrus-silver hover:shadow-lg hover:shadow-walrus-gray/20">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-walrus-silver mb-2">{title}</p>

          {isLoading ? (
            <div className="h-10 bg-walrus-gray rounded w-24 animate-pulse"></div>
          ) : (
            <p className="text-3xl font-bold text-walrus-white">{value}</p>
          )}

          {trend && !isLoading && (
            <div className={`mt-2 text-sm font-medium ${
              trend.direction === 'up' ? 'text-emerald-400' : 'text-red-400'
            }`}>
              <span>{trend.direction === 'up' ? '↑' : '↓'} {Math.abs(trend.value)}%</span>
            </div>
          )}
        </div>

        <div className="text-walrus-gray/50 h-12 w-12 flex items-center justify-center">
          {icon}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ICON COMPONENTS
// ============================================================================

const UsersIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`h-8 w-8 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M12 4.354a4 4 0 110 5.292M15 21H3.914a.97.97 0 01-.945-1.167l2.697-10.788A2 2 0 015.734 8h12.532a2 2 0 011.946 1.445l2.697 10.788a.97.97 0 01-.945 1.167M15 21h6m0 0h.01"
    />
  </svg>
);

const ActiveUsersIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`h-8 w-8 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
    />
  </svg>
);

const PhotosIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`h-8 w-8 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const AlbumsIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`h-8 w-8 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
    />
  </svg>
);

const StorageIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={`h-8 w-8 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
    />
  </svg>
);

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Admin stats component displaying dashboard metrics
 * Shows statistics cards with loading states
 *
 * @component
 * @example
 * <AdminStats stats={stats} isLoading={isLoading} />
 */
const AdminStatsComponent: React.FC<AdminStatsProps> = ({
  stats,
  isLoading = false,
}) => {
  // Debug logging
  useEffect(() => {
    logger.debug('[WALRUS] AdminStats: rendered', {
      isLoading,
      hasStats: !!stats,
    });
  }, [isLoading, stats]);

  /**
   * Format bytes to human-readable size
   */
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {/* Total Users */}
      <StatCard
        title="Total Users"
        value={isLoading ? '-' : stats?.totalUsers || 0}
        icon={<UsersIcon />}
        isLoading={isLoading}
      />

      {/* Active Users */}
      <StatCard
        title="Active Users"
        value={isLoading ? '-' : stats?.activeUsers || 0}
        icon={<ActiveUsersIcon />}
        isLoading={isLoading}
        trend={
          stats && stats.totalUsers > 0
            ? {
                value: Math.round((stats.activeUsers / stats.totalUsers) * 100),
                direction: 'up',
              }
            : undefined
        }
      />

      {/* Total Photos */}
      <StatCard
        title="Total Photos"
        value={isLoading ? '-' : stats?.totalPhotos || 0}
        icon={<PhotosIcon />}
        isLoading={isLoading}
      />

      {/* Total Albums */}
      <StatCard
        title="Total Albums"
        value={isLoading ? '-' : stats?.totalAlbums || 0}
        icon={<AlbumsIcon />}
        isLoading={isLoading}
      />

      {/* Storage Usage */}
      <StatCard
        title="Storage Used"
        value={isLoading ? '-' : formatBytes(stats?.storageUsed || 0)}
        icon={<StorageIcon />}
        isLoading={isLoading}
      />
    </div>
  );
};

AdminStatsComponent.displayName = 'AdminStats';

export default AdminStatsComponent;
